import type { SearchResultJobV5 } from '@seek/chalice-types';
import { Box, Stack } from 'braid-design-system';
import type { ComponentProps } from 'react';

import { ModifyMySearchAccessabilityLink } from 'src/components/SearchResultPage/SplitView/AccessabilityLinks/AccessabilityLinks';
import { useInFlowNudgingFeatureFlag } from 'src/store/featureFlags/hooks';
import { useSelector } from 'src/store/react';
import {
  selectCanonicalCompany,
  selectClearCompanyRefinements,
  selectIsRadialFilterNudgeShown,
} from 'src/store/selectors';

import CompanyResults from '../CompanyResults/CompanyResults';
import CompanySummary from '../CompanySummary/CompanySummary';
import { compareJobIds } from '../SplitView/helpers';

import { JobListItem } from './JobListItem/JobListItem';
import DistanceFromNudge from './Nudges/DistanceFromNudge/DistanceFromNudge';
import { NudgeIntent } from './Nudges/NudgeIntent/NudgeIntent';
import ProfileNudge from './Nudges/ProfileNudge/ProfileNudge';
import { useProfileNudge } from './Nudges/ProfileNudge/useProfileNudge';

interface SearchResultListProps
  extends Pick<ComponentProps<typeof JobListItem>, 'setCurrentJob'> {
  jobs: SearchResultJobV5[];
  isCompanySearch: boolean;
  selectedJobId?: string;
}

const getIndexOfFirstNonFeatured = (jobs: SearchResultJobV5[]): number =>
  jobs.findIndex((job) => !job.isFeatured);

const SearchResultList = ({
  jobs = [],
  setCurrentJob,
  selectedJobId,
  isCompanySearch,
}: SearchResultListProps) => {
  const { nudge: profileNudge } = useProfileNudge();
  const shouldShowProfileNudge = Boolean(profileNudge);
  const shouldShowDistanceFromNudge = useSelector(
    selectIsRadialFilterNudgeShown,
  );
  const canonicalCompany = useSelector(selectCanonicalCompany);
  const clearCompanyRefinements =
    useSelector(selectClearCompanyRefinements) || false;
  const companyName = canonicalCompany?.description;

  const profileNudgePosition = getIndexOfFirstNonFeatured(jobs) + 1;

  const jobList = jobs.map((job, index) => (
    <JobListItem
      position={index + 1}
      key={`${job.id}-${job.displayType}`}
      job={job}
      solMetadata={job.solMetadata}
      setCurrentJob={setCurrentJob}
      isSelected={compareJobIds(selectedJobId, job.id)}
    />
  ));

  const isNudgeIntentEnabled = useInFlowNudgingFeatureFlag();

  // TODO: Mock. Replace with real data source - DJS-1600
  const facetTypesData = [
    { id: 'lorem-ipsum-doloris', label: 'Lorem ipsum doloris', count: 88 },
    { id: 'lorem-ipsum-doloris', label: 'Lorem ipsum doloris', count: 100 },
    { id: 'lorem-ipsum-doloris', label: 'Lorem ipsum doloris', count: 88 },
    { id: 'lorem-ipsum-doloris', label: 'Lorem ipsum doloris', count: 200 },
    { id: 'lorem-ipsum-doloris', label: 'Lorem ipsum doloris', count: 88 },
    { id: 'lorem-ipsum-doloris', label: 'Lorem ipsum doloris', count: 66 },
    { id: 'lorem-ipsum-doloris', label: 'Lorem ipsum doloris', count: 88 },
    { id: 'lorem-ipsum-doloris', label: 'Lorem ipsum doloris', count: 55 },
    { id: 'lorem-ipsum-doloris', label: 'Lorem ipsum doloris', count: 88 },
  ];

  // TODO: Mock. Replace with real data source - DJS-1600
  const facets = {
    distinctTitle: facetTypesData,
    salaryMin: facetTypesData,
    workArrangement: facetTypesData,
    workType: facetTypesData,
  };

  // TODO: Mock title and description. Replace with copy in Figma - DJS-1677,1678,1679,1602
  const facetGroupInfo = {
    distinctTitle: {
      title: 'role',
      description: 'lorem ipsum role',
    },
    salaryMin: {
      title: 'salary',
      description: 'lorem ipsum salary',
    },
    workArrangement: {
      title: 'work arrangement',
      description: 'lorem ipsum work arrangement',
    },
    workType: {
      title: 'work type',
      description: 'lorem ipsum work type',
    },
  };

  // Combine facets and extra data from facetGroupMeta
  const facetDisplays = Object.entries(facets).map(([key, data]) => {
    const facetKey = key as keyof typeof facetGroupInfo;
    return {
      key,
      data,
      title: facetGroupInfo[facetKey].title,
      description: facetGroupInfo[facetKey].description,
    };
  });

  if (isNudgeIntentEnabled) {
    const nudgeIntentPosition = 4;
    facetDisplays.forEach((facet, i) => {
      const displayToastMessage =
        facet.key === 'distinctTitle' ? 'Search updated' : 'Filters updated';
      jobList.splice(
        nudgeIntentPosition + i, // TODO: Make it to every 4th card - DJS-1600
        0,
        <NudgeIntent
          key={facet.key}
          facetTypesData={facet.data}
          title={facet.title}
          description={facet.description}
          displayToastMessage={displayToastMessage}
        />,
      );
    });
  }
  // Only enabled existing nudges if the nudge intent is not enabled
  if (!isNudgeIntentEnabled) {
    if (shouldShowProfileNudge) {
      jobList.splice(
        profileNudgePosition,
        0,
        <ProfileNudge key="ProfileNudge" position={profileNudgePosition} />,
      );
    }

    if (shouldShowDistanceFromNudge) {
      const distanceFromNudgePosition = Math.floor(jobList.length / 2);

      jobList.splice(
        distanceFromNudgePosition,
        0,
        <DistanceFromNudge
          key="DistanceFromNudge"
          position={distanceFromNudgePosition}
        />,
      );
    }
  }

  return (
    <>
      {isCompanySearch && companyName ? (
        <Box paddingBottom="medium">
          <Stack space="small">
            <CompanySummary companyName={companyName} />
            {typeof clearCompanyRefinements === 'object' ? (
              <CompanyResults
                isCompanySearch={isCompanySearch}
                clearCompanyRefinements={clearCompanyRefinements}
                jobs={jobs || []}
              />
            ) : null}
          </Stack>
        </Box>
      ) : null}

      <ModifyMySearchAccessabilityLink placement="job-list" position="top" />

      <Stack space="xsmall">{jobList}</Stack>

      <ModifyMySearchAccessabilityLink placement="job-list" position="bottom" />
    </>
  );
};

export default SearchResultList;
